import { cn } from "@/lib/utils";
import { useParams } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { memo } from "react";
import { LibPathsEnum } from "../constants/lib.paths";
import { IOrganization } from "../models/lib.org";

interface NavOrganizationsComponentProps {
  organizations: IOrganization[];
  handleNavigate: (path: string) => void;
}

function NavOrganizationsComponent({
  organizations,
  handleNavigate,
}: NavOrganizationsComponentProps) {
  const { id } = useParams();
  const idNumber = Number(id);

  return (
    <SidebarProvider className="min-h-full">
      <SidebarGroup className="p-0">
        <SidebarMenu>
          {organizations.map((organization) => (
            <Collapsible
              key={organization.id}
              asChild
              open={true}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    className={cn(
                      idNumber === organization.id && "bg-gray-100 "
                    )}
                    tooltip={organization.name}
                    onClick={() =>
                      idNumber !== organization.id &&
                      handleNavigate(
                        LibPathsEnum.ORG_LIBRARY.replace(
                          ":id",
                          organization.id.toString()
                        )
                      )
                    }
                  >
                    <div className="h-10 flex items-center gap-2">
                      <Avatar className="rounded h-6 w-6">
                        <AvatarImage
                          className="rounded h-6 w-6 object-cover"
                          src={organization.photoUrl}
                        />
                        <AvatarFallback className="rounded text-[10px] text-white bg-primary h-6 w-6 uppercase">
                          {organization.name.slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <span
                        className="font-medium line-clamp-1 text-"
                        title={organization.name}
                      >
                        {organization.name}
                      </span>
                    </div>
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub className="pr-0 mr-0">
                    {organization.groups?.map((group) => (
                      <SidebarMenuSubItem
                        key={group.id}
                        onClick={() =>
                          idNumber !== group.id &&
                          handleNavigate(
                            LibPathsEnum.GROUP_LIBRARY.replace(
                              ":id",
                              group.id.toString()
                            )
                          )
                        }
                      >
                        <SidebarMenuSubButton
                          className={cn(
                            idNumber === group.id && "bg-gray-100",
                            "cursor-pointer"
                          )}
                        >
                          <div className="w-5 h-5 bg-orange-300 rounded" />
                          <span
                            className="line-clamp-1"
                            title={group.name}
                          >
                            {group.name}
                          </span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </SidebarProvider>
  );
}

export default memo(NavOrganizationsComponent);
